/**
 * Users — searchable/sortable table with Block/Unblock and a row-click Activity
 * Timeline drawer. On the free tier every mutation is a DIRECT updateDoc on the
 * user doc (admins may write `users` per the security rules):
 *   - Block   → { status: 'blocked' }
 *   - Unblock → { status: 'approved' }
 *   - Edit    → profile fields (name/mobile/email/company/city/gst)
 * Edit is now fully functional (the old "requires backend" stub is gone).
 *
 * Session counts are derived from app_open events in a rolling activity window.
 */
import { useMemo, useState, type ReactNode } from 'react';
import type { User, UserStatus } from '@mitang/shared';
import { useUsers, useActivitySince } from '../hooks/useFirestore';
import {
  useBlockUser,
  useDeleteUser,
  usePriceViewers,
  useTogglePriceAccess,
  useAdminUids,
  useToggleAdmin,
  useUnblockUser,
  useEditUser,
  type EditableUserFields,
} from '../hooks/useAdminActions';
import { sessionCountByUser } from '../lib/analytics';
import { daysAgo } from '../lib/time';
import { formatDate, timeAgo } from '../lib/format';
import { Card, SectionHeader, Button, StatusPill, Badge } from '../components/ui';
import { EmptyState, ErrorState, LoadingState } from '../components/States';
import { ActivityTimeline } from '../components/ActivityTimeline';

type SortKey = 'email' | 'name' | 'companyName' | 'city' | 'status' | 'lastActiveAt' | 'sessions';
type SortDir = 'asc' | 'desc';

const SESSION_WINDOW_DAYS = 30;

export function UsersPage(): JSX.Element {
  const users = useUsers();
  const activity = useActivitySince(useMemo(() => daysAgo(SESSION_WINDOW_DAYS), []));
  const block = useBlockUser();
  const deleteUser = useDeleteUser();
  const priceViewers = usePriceViewers();
  const togglePrice = useTogglePriceAccess();
  const adminUids = useAdminUids();
  const toggleAdmin = useToggleAdmin();
  const unblock = useUnblockUser();
  const edit = useEditUser();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatus | 'all'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('lastActiveAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [busyUid, setBusyUid] = useState<string | null>(null);
  const [drawerUser, setDrawerUser] = useState<User | null>(null);
  const [editUser, setEditUser] = useState<User | null>(null);

  const sessions = useMemo(() => sessionCountByUser(activity.data), [activity.data]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = users.data.filter((u) => {
      if (statusFilter !== 'all' && u.status !== statusFilter) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) ||
        u.companyName.toLowerCase().includes(q) ||
        u.mobile.toLowerCase().includes(q) ||
        u.city.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      switch (sortKey) {
        case 'sessions':
          return ((sessions.get(a.id) ?? 0) - (sessions.get(b.id) ?? 0)) * dir;
        case 'lastActiveAt':
          return ((a.lastActiveAt ?? 0) - (b.lastActiveAt ?? 0)) * dir;
        default:
          return String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? '')) * dir;
      }
    });
    return list;
  }, [users.data, search, statusFilter, sortKey, sortDir, sessions]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'email' || key === 'name' || key === 'companyName' || key === 'city' ? 'asc' : 'desc');
    }
  };

  const onBlockToggle = (u: User) => {
    setBusyUid(u.id);
    const opts = { onSettled: () => setBusyUid(null) };
    if (u.status === 'blocked') {
      unblock.mutate({ uid: u.id, name: u.name }, opts);
    } else {
      block.mutate({ uid: u.id, name: u.name }, opts);
    }
  };

  const onSaveEdit = (fields: EditableUserFields) => {
    if (!editUser) return;
    const u = editUser;
    edit.mutate(
      { uid: u.id, fields, name: fields.name || u.name },
      { onSuccess: () => setEditUser(null) },
    );
  };

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Users"
        subtitle="All dealer accounts. Click a row to view the activity timeline."
        actions={<Badge tone="neutral">{users.data.length} total</Badge>}
      />

      {/* Controls */}
      <Card className="p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name, company, mobile, city, email…"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 sm:max-w-md"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as UserStatus | 'all')}
            className="rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
          >
            <option value="all">All statuses</option>
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="blocked">Blocked</option>
            <option value="rejected">Rejected</option>
          </select>
          <span className="text-xs text-slate-500 sm:ml-auto">
            Showing {rows.length} of {users.data.length}
          </span>
        </div>
      </Card>

      {users.loading ? (
        <LoadingState label="Loading users…" />
      ) : users.error ? (
        <ErrorState message={users.error} />
      ) : users.data.length === 0 ? (
        <EmptyState title="No users yet" hint="Approved registrations become user accounts." />
      ) : rows.length === 0 ? (
        <EmptyState title="No matches" hint="Try a different search or status filter." />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <SortableTh label="Email" active={sortKey === 'email'} dir={sortDir} onClick={() => toggleSort('email')} />
                  <SortableTh label="Company" active={sortKey === 'companyName'} dir={sortDir} onClick={() => toggleSort('companyName')} />
                  <th className="px-4 py-3 font-medium">Mobile</th>
                  <SortableTh label="City" active={sortKey === 'city'} dir={sortDir} onClick={() => toggleSort('city')} />
                  <SortableTh label="Status" active={sortKey === 'status'} dir={sortDir} onClick={() => toggleSort('status')} />
                  <SortableTh label="Last active" active={sortKey === 'lastActiveAt'} dir={sortDir} onClick={() => toggleSort('lastActiveAt')} />
                  <SortableTh label="Sessions" active={sortKey === 'sessions'} dir={sortDir} onClick={() => toggleSort('sessions')} align="right" />
                  <th className="px-4 py-3 font-medium">Prices</th>
                  <th className="px-4 py-3 font-medium">Admin</th>
                  <th className="px-4 py-3 text-right font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((u) => {
                  const isBusy = busyUid === u.id;
                  return (
                    <tr
                      key={u.id}
                      onClick={() => setDrawerUser(u)}
                      className="cursor-pointer transition-colors hover:bg-slate-50"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-800">{u.email || '—'}</div>
                        <div className="text-xs text-slate-400">{u.name || (u.companyName ? '' : 'No profile yet')}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600">{u.companyName || '—'}</td>
                      <td className="px-4 py-3 font-mono text-[13px] text-slate-600">{u.mobile || '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{u.city || '—'}</td>
                      <td className="px-4 py-3">
                        <StatusPill status={u.status} />
                      </td>
                      <td className="px-4 py-3 text-slate-600" title={formatDate(u.lastActiveAt)}>
                        {u.lastActiveAt ? timeAgo(u.lastActiveAt) : '—'}
                      </td>
                      <td className="px-4 py-3 text-right tabular-nums text-slate-600">
                        {sessions.get(u.id) ?? 0}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const canSee = priceViewers.has(u.id);
                          const label = u.email || u.name || 'user';
                          return (
                            <button
                              type="button"
                              className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
                                canSee
                                  ? 'bg-emerald-100 text-emerald-700'
                                  : 'bg-slate-100 text-slate-500'
                              }`}
                              title={
                                canSee
                                  ? 'Can see prices — click to revoke'
                                  : 'Cannot see prices — click to grant'
                              }
                              onClick={() => {
                                const ok = window.confirm(
                                  canSee
                                    ? `Hide prices from ${label}?\n\nThey will no longer see any prices in the app.`
                                    : `Show prices to ${label}?\n\nThey will see dealer prices on every product.`,
                                );
                                if (!ok) return;
                                togglePrice.mutate({
                                  uid: u.id,
                                  email: u.email,
                                  grant: !canSee,
                                  label,
                                });
                              }}
                            >
                              {canSee ? 'VISIBLE' : 'HIDDEN'}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        {(() => {
                          const isAdmin = adminUids.has(u.id);
                          const label = u.email || u.name || 'user';
                          return (
                            <button
                              type="button"
                              className={`rounded-full px-2.5 py-1 text-[11px] font-bold transition ${
                                isAdmin ? 'bg-brand-100 text-brand-700' : 'bg-slate-100 text-slate-500'
                              }`}
                              title={isAdmin ? 'Admin — click to remove' : 'Not an admin — click to make admin'}
                              onClick={() => {
                                const ok = window.confirm(
                                  isAdmin
                                    ? `Remove admin rights from ${label}?\n\nThey will lose access to this panel.`
                                    : `Make ${label} an admin?\n\nThey will get FULL access to this panel: dealers, prices, catalog, notifications.`,
                                );
                                if (!ok) return;
                                toggleAdmin.mutate({
                                  uid: u.id,
                                  email: u.email,
                                  name: u.name,
                                  grant: !isAdmin,
                                });
                              }}
                            >
                              {isAdmin ? 'ADMIN' : '—'}
                            </button>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3">
                        <div
                          className="flex justify-end gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button variant="secondary" size="sm" onClick={() => setEditUser(u)}>
                            Edit
                          </Button>
                          {u.status === 'blocked' ? (
                            <Button
                              variant="success"
                              size="sm"
                              loading={isBusy && unblock.isPending}
                              disabled={isBusy}
                              onClick={() => onBlockToggle(u)}
                            >
                              Unblock
                            </Button>
                          ) : (
                            <Button
                              variant="danger"
                              size="sm"
                              loading={isBusy && block.isPending}
                              disabled={isBusy || u.status === 'rejected'}
                              onClick={() => onBlockToggle(u)}
                            >
                              Block
                            </Button>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            loading={isBusy && deleteUser.isPending}
                            disabled={isBusy}
                            onClick={() => {
                              const label = u.email || u.name || 'this user';
                              if (
                                window.confirm(
                                  `Delete ${label} permanently?\n\nThis removes their sign-in and profile. It does NOT stop them signing up again — use Block for that.`,
                                )
                              ) {
                                setBusyUid(u.id);
                                deleteUser.mutate(
                                  { uid: u.id, label },
                                  { onSettled: () => setBusyUid(null) },
                                );
                              }
                            }}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <ActivityTimeline user={drawerUser} open={drawerUser !== null} onClose={() => setDrawerUser(null)} />

      {editUser && (
        <EditUserModal
          user={editUser}
          saving={edit.isPending}
          onCancel={() => setEditUser(null)}
          onSave={onSaveEdit}
        />
      )}
    </div>
  );
}

function EditUserModal({
  user,
  saving,
  onCancel,
  onSave,
}: {
  user: User;
  saving: boolean;
  onCancel: () => void;
  onSave: (fields: EditableUserFields) => void;
}): JSX.Element {
  const [name, setName] = useState(user.name);
  const [mobile, setMobile] = useState(user.mobile);
  const [email, setEmail] = useState(user.email);
  const [companyName, setCompanyName] = useState(user.companyName);
  const [city, setCity] = useState(user.city);
  const [gstNumber, setGstNumber] = useState(user.gstNumber ?? '');

  const canSave =
    name.trim() !== '' &&
    mobile.trim() !== '' &&
    email.trim() !== '' &&
    companyName.trim() !== '' &&
    city.trim() !== '';

  const submit = () => {
    if (!canSave || saving) return;
    onSave({
      name: name.trim(),
      mobile: mobile.trim(),
      email: email.trim(),
      companyName: companyName.trim(),
      city: city.trim(),
      gstNumber: gstNumber.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div
        className="absolute inset-0 bg-slate-900/40"
        onClick={() => !saving && onCancel()}
        aria-hidden
      />
      <div className="relative w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-xl">
        <h3 className="text-base font-semibold text-slate-900">Edit dealer</h3>
        <p className="mt-1 text-sm text-slate-500">
          Update <span className="font-medium">{user.name}</span>&rsquo;s profile. Changes are
          written directly to the user record.
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EditField label="Name" value={name} onChange={setName} />
          <EditField label="Mobile" value={mobile} onChange={setMobile} mono />
          <EditField label="Email" value={email} onChange={setEmail} type="email" />
          <EditField label="Company" value={companyName} onChange={setCompanyName} />
          <EditField label="City" value={city} onChange={setCity} />
          <EditField label="GST (optional)" value={gstNumber} onChange={setGstNumber} mono />
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={submit} loading={saving} disabled={!canSave}>
            Save changes
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditField({
  label,
  value,
  onChange,
  type = 'text',
  mono,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  mono?: boolean;
}): ReactNode {
  return (
    <div>
      <label className="mb-1 block text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500 ${
          mono ? 'font-mono text-[13px]' : ''
        }`}
      />
    </div>
  );
}

function SortableTh({
  label,
  active,
  dir,
  onClick,
  align = 'left',
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
  align?: 'left' | 'right';
}): JSX.Element {
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={onClick}
        className={`inline-flex items-center gap-1 hover:text-slate-700 ${active ? 'text-slate-800' : ''}`}
      >
        {label}
        {active && <span aria-hidden>{dir === 'asc' ? '▲' : '▼'}</span>}
      </button>
    </th>
  );
}
